/**
 * The note under a job's example companies, from `/ai/example-companies`: the companies Coresignal didn't find, and
 * when the examples disagree with the size band or the industries the job prefers. The examples and the band both
 * rank people higher, so the note says so and leaves the choice to the user, as the chat's agent asks it.
 */

import type { components } from '@/types/apiSchema';

import { type EmployerSizeBand, employerSizeOptions } from './types.ts';

export type ExampleCompaniesCheck =
  components['schemas']['ExampleCompaniesCheckResponse'];

const sizeLabel = (band: string) => {
  const label =
    employerSizeOptions.find((option) => option.value === band)?.label ?? band;
  return label.charAt(0).toLowerCase() + label.slice(1);
};

const listed = (names: string[]) =>
  names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;

/** The note's sentences, in order; none when every company was found and nothing disagrees. */
export function exampleCompaniesNote(
  check: ExampleCompaniesCheck,
  employerSize: EmployerSizeBand,
): string[] {
  const sentences: string[] = [];
  const missing = check.companies
    .filter((company) => !company.found)
    .map((company) => company.name);
  if (missing.length > 0) {
    sentences.push(
      `We couldn't find ${listed(missing)}, so ${missing.length > 1 ? "they don't" : "it doesn't"} count in the search. Adding a website in brackets after the name helps.`,
    );
  }

  const changes: string[] = [];
  if (check.sizeDisagrees) {
    sentences.push(
      check.examplesSize
        ? `Your examples have ${sizeLabel(check.examplesSize)}, but you picked ${sizeLabel(employerSize)}.`
        : `None of your examples has ${sizeLabel(employerSize)}, the size you picked.`,
    );
    changes.push('size');
  }
  if (check.industriesDisagree) {
    sentences.push('None of your examples is in the industries you picked.');
    changes.push('industries');
  }
  if (changes.length > 0) {
    sentences.push(
      `The search ranks people from both higher, so you can keep both, or change the ${changes.join(' and ')}.`,
    );
  }
  return sentences;
}
